import classNames from 'classnames';
import React, {ReactNode} from 'react';

import HoverIcon from 'nav/expanded/HoverIcon';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Clipboard from 'ui-library/Clipboard';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {ElementMouseEvent} from 'util/Types';

export interface ClipboardTextProps {
  children: ReactNode;
  className?: string;
  clipboardData: string;
  message?: string;
  onClick?: (e: ElementMouseEvent) => void;
  tooltipPlacement?: FloatingPlacement;
  // Default false; if true, the copy icon will be visible regardless of whether or not the text is hovered.
  // Useful in situations where the icon visibility should depend on the hover state of something other than
  // the children.
  copyIconAlwaysVisible?: boolean;
  // Default false; this prop gives a way to improve performance when rendering lots of ClipboardTexts
  // since rendering tooltips is expensive. Instead, you can delay rendering the tooltips by starting
  // disableTooltip as true then changing it to false once the tooltip is needed, for example when the user's
  // mouse is hovering a wrapping div.
  disableTooltip?: boolean;
}

export default function ClipboardText({
  message = 'Successfully copied to the clipboard',
  children,
  className,
  clipboardData,
  onClick,
  tooltipPlacement = FloatingPlacement.TOP,
  copyIconAlwaysVisible = false,
  disableTooltip = false,
}: ClipboardTextProps): JSX.Element {
  function notifyCopied(): void {
    notifications.success(message);
  }

  return (
    <div
      className={classNames(
        'clipboardIcon-container',
        copyIconAlwaysVisible && 'clipboardIcon--force-visibility',
        className
      )}
      {...getTestProps({component: 'ClipboardText', element: 'root'})}
    >
      {children}
      <Tooltip text="Copy to clipboard" placement={tooltipPlacement} isDisabled={disableTooltip}>
        <Clipboard value={clipboardData} notifyCopied={notifyCopied} onClick={onClick}>
          <HoverIcon className="clipboardIcon" icon="copy" />
        </Clipboard>
      </Tooltip>
    </div>
  );
}
